import React, { useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { Box, Link, Stack, Typography } from '@mui/material'
import { PencilIcon, SendIcon, Trash2Icon } from 'lucide-react'
import { OUTREACH_TARGET_STATUSES } from '@repo/types'
import { EMPTY_VALUE } from '@repo/ui'

import { ActionDropdown, ConfirmSettingModal, EmptyState, StatusChip, Table } from 'components'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { OUTREACH_CAMPAIGN_TYPE_CHIPS, OUTREACH_TARGET_STATUS_CHIPS } from 'constants/statuses'
import { useSnackbar } from 'contexts'
import { useReferrerState } from 'hooks'
import { useOutreachActions, useOutreachCampaigns } from 'hooks/api/useGtmOutreach'
import { ROUTES } from 'routes'
import type { OutreachCampaign } from 'services'
import { getErrorMessage } from 'utils'

import { CampaignModal } from './outreachModals'
import { formatCampaignDates, formatTripCost } from './outreachModel'

const CellText = ({ children }: { children: React.ReactNode }) => (
  <Stack justifyContent="center" height="100%">
    <Typography variant="body3" noWrap>
      {children}
    </Typography>
  </Stack>
)

/**
 * The campaign's name (a link to it) over its city. VRG-684 folded City into this cell so the
 * table fits 1280 with the per-status chips and the new Trip-cost column (measured: the separate
 * City column pushed Trip cost off-screen behind a sideways scroll).
 */
const CampaignCell = ({ row }: { row: OutreachCampaign }) => {
  const referrer = useReferrerState()
  return (
    <Stack justifyContent="center" sx={{ height: '100%', minWidth: 0 }}>
      <Link
        component={RouterLink}
        to={ROUTES.INTERNAL_GTM_SALES_CAMPAIGN(row.id)}
        state={referrer}
        variant="body3"
        noWrap
        sx={{ fontWeight: 500, color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
      >
        {row.name}
      </Link>
      {row.city ? (
        <Typography variant="body4" color="text.secondary" noWrap>
          {row.city}
        </Typography>
      ) : null}
    </Stack>
  )
}

export const OUTREACH_ADMIN_TIP = 'Only a Vera admin can change outreach campaigns.'

/** "Replied 1 · Meeting held 1": the full breakdown, for the cell's hover title. */
const standingText = (row: OutreachCampaign) =>
  OUTREACH_TARGET_STATUSES.filter((s) => row.counts[s] > 0)
    .map((s) => `${OUTREACH_TARGET_STATUS_CHIPS[s].label} ${row.counts[s]}`)
    .join(' · ')

/**
 * The campaign's brands: the total, then a chip per status that has any, in pipeline order, each
 * carrying its count ("Sent 3", "Meeting held 1"). At most two chips fit beside the 1280 layout's
 * other columns, so the two furthest-along show and the rest read "+N"; the full breakdown is the
 * cell's hover title.
 *
 * VRG-684 — this replaced VRG-679's Brands column plus one column per status. With eight statuses
 * (three added: meeting held, pilot, LOI signed) those columns, each sized for its header (§49),
 * came to ~1,100px on their own, so at 1280 the table scrolled sideways for figures that are
 * mostly zero. The per-status sort is the cost, accepted because the list is a handful of
 * campaigns, ordered by date.
 */
/** Chips shown per row; the rest are named in "+N" and in the hover title. */
const MAX_STATUS_CHIPS = 2

const BrandsCell = ({ row }: { row: OutreachCampaign }) => {
  const present = OUTREACH_TARGET_STATUSES.filter((s) => row.counts[s] > 0)
  // The furthest-along statuses are the informative ones ("1 LOI signed" says more than
  // "4 sent"), so when they do not all fit, the last ones in pipeline order are kept.
  const shown = present.slice(-MAX_STATUS_CHIPS)
  const hidden = present.length - shown.length
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      title={present.length ? standingText(row) : undefined}
      sx={{ height: '100%', flexWrap: 'nowrap', overflow: 'hidden' }}
    >
      <Typography variant="body3" sx={{ fontWeight: 500, minWidth: 20, flexShrink: 0 }}>
        {row.total}
      </Typography>
      {hidden > 0 && (
        <Typography variant="body4" color="text.secondary" sx={{ flexShrink: 0 }}>
          +{hidden}
        </Typography>
      )}
      {shown.map((s) => (
        <Box key={s} sx={{ flexShrink: 0 }}>
          <StatusChip
            size="small"
            statuses={{ [s]: { ...OUTREACH_TARGET_STATUS_CHIPS[s], label: `${OUTREACH_TARGET_STATUS_CHIPS[s].label} ${row.counts[s]}` } }}
            status={s}
          />
        </Box>
      ))}
    </Stack>
  )
}

/**
 * Sales → Campaigns (`/internal/gtm/sales?tab=campaigns`) — VRG-679's campaigns list, moved inside
 * Sales by VRG-684 (Spencer: Outreach is part of Sales, not its own row) and labelled Campaigns
 * since VRG-713 (it was the Outreach tab). Traction channel #13 as
 * Vera runs it: a campaign is email outreach or an in-person city trip (a field visit); each lists
 * target brands with a status. Linking a reply or a meeting to People or to a demo records source
 * channel Sales (#13, stored as `outreach`), which the Pipeline tab counts. No email is sent from here.
 *
 * The page owns the header's "New campaign" button (§26: the primary action lives in the header),
 * so the create dialog's open state comes in as props.
 */
export const OutreachCampaignsPanel = ({
  isAdmin,
  creating,
  onCreatingChange,
}: {
  isAdmin: boolean
  creating: boolean
  onCreatingChange: (open: boolean) => void
}) => {
  const navigate = useNavigate()
  const { open: notify } = useSnackbar()
  const { data: campaigns = [], isLoading, error } = useOutreachCampaigns()
  const actions = useOutreachActions()
  const [editing, setEditing] = useState<OutreachCampaign | null>(null)
  const [deleting, setDeleting] = useState<OutreachCampaign | null>(null)
  const fail = (e: Error) => notify({ type: 'error', title: getErrorMessage(e) })
  const modalOpen = creating || editing !== null
  const closeModal = () => {
    setEditing(null)
    onCreatingChange(false)
  }

  const columns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Campaign',
        type: 'string',
        width: 200,
        minWidth: 170,
        flex: 200,
        pinned: 'left',
        disableReorder: true,
        renderCell: ({ row }: { row: OutreachCampaign }) => <CampaignCell row={row} />,
      },
      {
        field: 'type',
        headerName: 'Type',
        type: 'statusChip',
        options: statusFilterOptions(OUTREACH_CAMPAIGN_TYPE_CHIPS),
        width: 120,
        renderCell: ({ row }: { row: OutreachCampaign }) => (
          <Stack justifyContent="center" height="100%">
            <Box>
              <StatusChip size="small" statuses={OUTREACH_CAMPAIGN_TYPE_CHIPS} status={row.type} />
            </Box>
          </Stack>
        ),
      },
      {
        field: 'starts_on',
        headerName: 'Dates',
        type: 'string',
        width: 160,
        valueGetter: (_v: unknown, row: OutreachCampaign) => row.starts_on ?? '',
        renderCell: ({ row }: { row: OutreachCampaign }) => <CellText>{formatCampaignDates(row.starts_on, row.ends_on)}</CellText>,
      },
      {
        field: 'total',
        headerName: 'Brands',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        width: 320,
        minWidth: 300,
        flex: 320,
        renderCell: ({ row }: { row: OutreachCampaign }) => <BrandsCell row={row} />,
      },
      {
        field: 'trip_cost',
        headerName: 'Trip cost',
        type: 'number',
        width: 130,
        valueGetter: (_v: unknown, row: OutreachCampaign) => row.trip_cost?.amount ?? null,
        renderCell: ({ row }: { row: OutreachCampaign }) => (
          <Stack justifyContent="center" alignItems="flex-end" height="100%">
            <Typography variant="body3" noWrap>
              {formatTripCost(row.trip_cost) ?? EMPTY_VALUE}
            </Typography>
          </Stack>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 54,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        disableReorder: true,
        align: 'center',
        pinned: 'right',
        renderCell: ({ row }: { row: OutreachCampaign }) => (
          <ActionDropdown
            items={[
              { label: 'Edit campaign', icon: <PencilIcon strokeWidth={1} />, disabled: !isAdmin, tooltip: isAdmin ? undefined : OUTREACH_ADMIN_TIP, onClick: () => setEditing(row) },
              { label: 'Delete campaign', icon: <Trash2Icon strokeWidth={1} />, destructive: true, divider: true, disabled: !isAdmin, tooltip: isAdmin ? undefined : OUTREACH_ADMIN_TIP, onClick: () => setDeleting(row) },
            ]}
          />
        ),
      },
    ],
    [isAdmin],
  )

  const status = (error as (Error & { status?: number }) | null)?.status

  return (
    <>
      <Table
        loading={isLoading}
        columns={columns}
        rows={campaigns}
        alwaysShowRowSelection={false}
        fillBottom
        paginationMode="client"
        initialPageSize={50}
        viewKey="internal-gtm-outreach"
        emptyState={
          <EmptyState
            title={status === 403 ? 'You cannot view campaigns' : error ? 'Could not load campaigns' : 'No campaigns yet'}
            text={
              status === 403
                ? 'Campaigns need the "View GTM" permission and a Vera staff role.'
                : error
                  ? 'The request failed. Reload the page, or check the API is running.'
                  : 'Create a campaign for an email push or a city trip, then add the brands it targets.'
            }
            icon={<SendIcon strokeWidth={1} size={32} />}
          />
        }
      />

      <CampaignModal
        open={modalOpen}
        campaign={editing}
        loading={actions.createCampaign.isPending || actions.updateCampaign.isPending}
        onClose={closeModal}
        onSave={(input) => {
          if (!editing) {
            actions.createCampaign.mutate(input, {
              onSuccess: ({ id }) => {
                closeModal()
                navigate(ROUTES.INTERNAL_GTM_SALES_CAMPAIGN(id))
              },
              onError: fail,
            })
          } else {
            actions.updateCampaign.mutate(
              { id: editing.id, input },
              { onSuccess: () => { closeModal(); notify({ type: 'success', title: 'Campaign saved' }) }, onError: fail },
            )
          }
        }}
      />
      <ConfirmSettingModal
        open={!!deleting}
        onClose={() => setDeleting(null)}
        destructive
        loading={actions.deleteCampaign.isPending}
        title="Delete campaign?"
        confirmLabel="Delete"
        consequence={
          <>
            <strong>{deleting?.name}</strong> and its {deleting?.total ?? 0} brands are deleted. Source channels already
            recorded on People and Companies stay.
          </>
        }
        onConfirm={() =>
          deleting &&
          actions.deleteCampaign.mutate(deleting.id, {
            onSuccess: () => { setDeleting(null); notify({ type: 'success', title: 'Campaign deleted' }) },
            onError: fail,
          })
        }
      />
    </>
  )
}
